import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import { State } from '../reducers'
import { Profile } from '../types/profile'
import { Subscription } from '../types/subscription'
import { type EntityId, idKey } from '../types/constants'
import { Marketplace, MarketplaceI, Marketplaces } from '../util/marketplaces'
import RoundingStrategy = Models.RoundingStrategy

export interface useUserValues {
    hasUser: boolean
    invalidKeys: boolean
    missingUser: boolean
    loading: boolean
    user: Models.User | null
    profile: EntityId | null
    hasKeys: boolean
    settings: Models.Settings | null
    smartBidSettings: Models.SmartBidSetting | null
    profiles: Profile[]
    activeProfile: Profile | null
    screenshotMode: boolean
    marketplace: MarketplaceI | null
    activeSubscription: Subscription | null
    hasActiveSubscription: boolean
    searchParams: string
    roundingStrategies: RoundingStrategy
    is_kdp: boolean
}

export const userSelector = createSelector(
    (store: State) => store.user.active_subscription,
    (store: State) => store.user.profile,
    (store: State) => store.user.fetching,
    (store: State) => store.user.loaded,
    (store: State) => store.user.current,
    (store: State) => store.user.has_active_subscription,
    (store: State) => store.user.screenshot_mode,
    (store: State) => store.user.search_params,
    (
        activeSubscription,
        profile,
        loading,
        userLoaded,
        user,
        hasActiveSubscription,
        screenshotMode,
        searchParams,
    ) => {
        const profiles = user?.keys?.profiles || []
        const firstProfile = profiles.length > 0 ? profiles[0] : undefined
        const activeProfile = profiles?.find((p) => idKey(p.profile_id) === idKey(profile))
        const settings = activeProfile?.settings || firstProfile?.settings
        const smartBidSettings = activeProfile?.smart_bid_settings?.[0]
        const marketplace = activeProfile?.marketplace_id
            ? Marketplaces[activeProfile.marketplace_id as Marketplace]
            : null
        const roundingStrategies = {
            bulk_actions_rounding_strategy:
                activeProfile?.settings?.bulk_actions_rounding_strategy ?? 3,
            recipes_rounding_strategy: activeProfile?.settings?.recipes_rounding_strategy ?? 3,
            promotions_rounding_strategy:
                activeProfile?.settings?.promotions_rounding_strategy ?? 3,
            smart_bids_rounding_strategy:
                activeProfile?.settings?.smart_bids_rounding_strategy ?? 3,
        }
        window.isKdp = activeProfile?.is_kdp ?? false

        const hasUser = user && userLoaded
        const missingUser = !user && userLoaded
        const hasKeys = !!user?.keys

        let invalidKeys = false
        if (!!user?.keys && !user.keys.is_valid) {
            invalidKeys = true
        }

        const r: useUserValues = {
            hasUser: hasUser ?? false,
            missingUser,
            loading,
            user,
            profile,
            hasKeys,
            settings: settings ?? null,
            smartBidSettings: smartBidSettings ?? null,
            profiles,
            activeProfile: activeProfile ?? null,
            screenshotMode,
            marketplace,
            activeSubscription,
            hasActiveSubscription,
            searchParams,
            roundingStrategies,
            invalidKeys,
            is_kdp: activeProfile?.is_kdp ?? false,
        }
        return r
    },
)

export const useUser = (): useUserValues => {
    return useSelector(userSelector)
}
